import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import moment from 'moment';
import QRCode from 'qrcode';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import 'sweetalert2/src/sweetalert2.scss';
import './Orders.css';

import { API_BASE } from '../config';
import { scopeStations } from '../components/roleAccess';
import { tr, trc } from '../i18n';

// Same auth pattern as Cards.js — never captured once and reused, always
// read fresh at the moment of each request so a background token refresh
// is picked up instead of silently 401ing on a stale copy.
const authToken = () => {
  try {
    return JSON.parse(sessionStorage.getItem('token')).data.access_token;
  } catch (e) {
    return '';
  }
};

// Orders share the SAME FuelCard table/endpoint as regular cards — an order
// IS a FuelCard, just one whose card_id starts with ORD- (server-generated,
// see _create_order() / generate_order_card_id() in cards.py) and whose
// preset_value/credit are locked after creation. This page only ever shows
// and creates that subset.
const isOrderCardId = (cardId) => typeof cardId === 'string' && cardId.startsWith('ORD-');

// created_at arrives as a NAIVE "YYYY-MM-DD HH:mm:ss" string — str(card.created_at)
// in cards.py — with no zone marker on it at all. A plain moment(v) parses a bare
// string like that as BROWSER-LOCAL, which silently reinterprets whatever zone
// MySQL wrote it in as the viewer's own zone and shows a time off by the
// difference. Parsed explicitly as UTC (what NOW() writes in the container) and
// converted to the viewer's zone, so Istanbul staff and the Tanzania stations
// each see their own correct local time off the same stored value.
//
// ! If `SELECT NOW()` on the DB matches your wall clock rather than trailing it,
// the DB is storing EAT already — in that case drop .utc()/.local() here and use
// plain moment(v), because no conversion is wanted.
const orderTime = (v) => (v ? moment.utc(v).local().format('MMM D, YYYY h:mm A') : '—');

// Shared by the Orders list AND OrderModal's bulk-create rows — both switch
// from a horizontally-scrolling table to a vertical, no-scroll layout below
// the same breakpoint (576px, Bootstrap's own `sm`), so one hook keeps them
// in agreement instead of two components tracking window width separately.
const useIsPhone = (breakpoint = 576) => {
  const [isPhone, setIsPhone] = useState(
    typeof window !== 'undefined' && window.innerWidth <= breakpoint
  );
  useEffect(() => {
    const onResize = () => setIsPhone(window.innerWidth <= breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isPhone;
};

// station_serial can arrive as an array, a single string, or a comma-joined
// string — normalize all of them to a clean string[].
const toSerialArray = (v) =>
  Array.isArray(v)
    ? v.map((s) => String(s).trim()).filter(Boolean)
    : typeof v === 'string' && v.trim()
    ? v.split(',').map((s) => s.trim()).filter(Boolean)
    : [];

// ─── Station picker — identical to Cards.js's SerialField ────────────────
const SerialField = ({ serials, setSerials, stations, stationName }) => {
  const [pendingText, setPendingText] = useState('');
  const [pickerValue, setPickerValue] = useState('');

  const addSerial = (raw) => {
    const s = String(raw || '').trim();
    if (!s) return;
    setSerials(serials.includes(s) ? serials : [...serials, s]);
  };
  const removeSerial = (s) => setSerials(serials.filter((x) => x !== s));

  const available = stations.filter((s) => !serials.includes(s.serial_number));
  return (
    <Form.Group className="mb-3">
      <Form.Label>{tr("Station Serials *")}</Form.Label>
      {serials.length > 0 && (
        <div className="mb-2 d-flex flex-wrap" style={{ gap: '0.4rem' }}>
          {serials.map((s) => (
            <span
              key={s}
              className="badge bg-primary d-inline-flex align-items-center"
              style={{ fontSize: '0.85em', fontWeight: 500 }}
              title={s}
            >
              {stationName(s)}
              <button
                type="button"
                aria-label={tr("Remove {0}", s)}
                onClick={() => removeSerial(s)}
                style={{
                  marginLeft: '0.5em',
                  border: 'none',
                  background: 'transparent',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '1.1em',
                  lineHeight: 1,
                  padding: 0,
                }}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <Row>
        <Col md={7}>
          <Form.Control
            as="select"
            value={pickerValue}
            onChange={(e) => {
              addSerial(e.target.value);
              setPickerValue('');
            }}
          >
            <option value="">
              {available.length ? tr("Add a station…") : tr("All stations added")}
            </option>
            {available.map((s) => (
              <option key={s.serial_number} value={s.serial_number}>
                {s.name} ({s.serial_number})
              </option>
            ))}
          </Form.Control>
        </Col>
        <Col md={5}>
          <div className="d-flex" style={{ gap: '0.5rem' }}>
            <Form.Control
              type="text"
              value={pendingText}
              placeholder={tr("or type a serial")}
              onChange={(e) => setPendingText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ',') {
                  e.preventDefault();
                  addSerial(pendingText);
                  setPendingText('');
                }
              }}
            />
            <Button
              variant="outline-secondary"
              onClick={() => {
                addSerial(pendingText);
                setPendingText('');
              }}
            >
              {tr("Add")}
            </Button>
          </div>
        </Col>
      </Row>
      <Form.Text className="text-muted">
        {tr("One order can be valid at several stations. Pick from the list or type a serial and press Enter.")}
      </Form.Text>
    </Form.Group>
  );
};

// ─── Create Order ─────────────────────────────────────────────────────────
// A separate, small form — an order only ever needs how much, which
// product, whose plate, which station(s). card_id, is_customer, payment
// method etc. are all forced server-side; see _create_order() in cards.py.
// payment_method, preset_type, is_customer and is_order are fixed for every
// order and never exposed in the form — they're set server-side at submit
// time (see submit() below).
//
// license_plate and tin_number are two DIFFERENT things and are never
// copied into one another: license_plate is the vehicle's registration
// plate, tin_number is the customer's tax identification number.
const emptyRow = () => ({
  amount: '',
  product: '',       // must now be chosen before submit — see ORDER_FIELDS
  license_plate: '',
  tin_number: '',
  customer_name: '', // labelled "Driver Name" in the UI; the backend/DB field
                     // is customer_name, so the key stays as-is
});

// Every field on an order is mandatory. Kept as ONE list, in field order, so
// the create form and the edit form cannot drift apart. The labels here are
// what the operator is told is missing, so they match the <Form.Label> text
// rather than the DB column name.
//
// ! `product` being in this list changes BEHAVIOUR, not just the form: an
// empty product used to mean "either Diesel or Unleaded is accepted at the
// pump", which is a real operating mode, not a blank. Every order created
// from now on is locked to one fuel, and a driver at the wrong nozzle will
// be refused. To go back to allowing either, remove the 'product' line below
// AND restore `<option value="">Either (Diesel or Unleaded)</option>` as the
// first option in both product selects.
const ORDER_FIELDS = [
  ['amount',        trc('litres', 'Amount')],
  ['product',       tr('Product')],
  ['license_plate', tr('License Plate')],
  ['tin_number',    tr('TIN Number')],
  ['customer_name', tr('Driver Name')],
];

// The edit form cannot touch the amount — cards.py rejects a preset_value
// change on an order outright — so it validates everything except that.
const EDIT_FIELDS = ORDER_FIELDS.filter(([key]) => key !== 'amount');

// Which of `fields` are blank or whitespace-only, as display labels.
// Checked in JS and not left to the `required` attribute alone, because the
// browser happily accepts a field containing nothing but spaces — which then
// reaches the server as '' after .trim() and defeats the whole point.
const missingFields = (row, fields) =>
  fields
    .filter(([key]) => !String(row[key] ?? '').trim())
    .map(([, label]) => label);

// ─── Duplicate rules ──────────────────────────────────────────────────────
// A vehicle/customer may hold only ONE open order PER STATION. While an order
// exists for a plate or a TIN at a station, another one with the same value
// for that same station is refused — the existing one has to be spent at the
// pump first, at which point the backend removes it and the value is free
// again.
//
// Scoped per station, not globally: the same lorry legitimately runs between
// sites and may hold an open order at each. Only an overlap of stations is a
// real clash. See sharesStation() below.
//
// Driver Name is deliberately NOT in this list: two different vehicles can
// legitimately share a driver, and blocking on it would refuse valid orders.
const DUPLICATE_FIELDS = [
  ['license_plate', tr('License Plate')],
  ['tin_number',    tr('TIN Number')],
];

// True when `order` is valid at any of `serials`. An order can carry several
// stations and so can the batch being created, so this is an intersection
// test, not an equality one — one station in common is enough to clash.
const sharesStation = (order, serials) => {
  if (!serials || serials.length === 0) return false;
  return toSerialArray(order.station_serial).some((s) => serials.includes(s));
};

// Plates are typed inconsistently — "T 324 DLS", "t324dls", "T-324-DLS" are
// one vehicle, and a comparison that missed that would let the same plate
// through simply by spacing it differently. Case and every non-alphanumeric
// character are stripped before comparing. Empty stays empty, so a blank
// field never collides with another blank field.
const normalizeKey = (v) => String(v ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');

// value -> the order holding it, per duplicate field. Built once per list
// rather than scanned per keystroke, so typing stays responsive with a few
// thousand open orders.
const buildOrderIndex = (orders) => {
  const index = {};
  DUPLICATE_FIELDS.forEach(([key]) => { index[key] = new Map(); });
  (orders || []).forEach((o) => {
    DUPLICATE_FIELDS.forEach(([key]) => {
      const k = normalizeKey(o[key]);
      if (k && !index[key].has(k)) index[key].set(k, o);
    });
  });
  return index;
};

// Every clash between the rows being created and (a) the orders that already
// exist, and (b) EACH OTHER. The second half matters as much as the first: a
// bulk submit of 10 rows can hold the same plate twice, and checking only
// against the server would wave that through and create both.
//
// Returns every conflict, not just the first, so one dialog can list them all
// instead of making the operator fix and resubmit one at a time.
const findConflicts = (rows, index) => {
  const seen = {};
  DUPLICATE_FIELDS.forEach(([key]) => { seen[key] = new Map(); });

  const conflicts = [];
  rows.forEach((row, i) => {
    DUPLICATE_FIELDS.forEach(([key, label]) => {
      const k = normalizeKey(row[key]);
      if (!k) return;

      const existing = index && index[key] && index[key].get(k);
      if (existing) {
        conflicts.push({
          rowNo: i + 1, key, label,
          value: String(row[key]).trim(),
          against: existing.card_id,
          againstKind: 'order',
          order: existing,   // kept so the message can name the station
        });
        return;
      }

      const earlierRow = seen[key].get(k);
      if (earlierRow !== undefined) {
        conflicts.push({
          rowNo: i + 1, key, label,
          value: String(row[key]).trim(),
          against: tr('row {0}', earlierRow + 1),
          againstKind: 'row',
        });
        return;
      }

      seen[key].set(k, i);
    });
  });
  return conflicts;
};

// ─── License plate format ─────────────────────────────────────────────────
// Mirrors normalize_plate() / PLATE_PATTERNS in cards.py. Three shapes,
// capitals only:
//
//     A123ABC   one letter, three digits, three letters   (T324DLS)
//     AB12      two letters, two digits
//     ABC12     three letters, two digits
//
// Checked after separators are removed, so "T 324 DLS" is accepted and sent
// as T324DLS. The server applies the identical rule — this copy exists to
// tell the operator while they are typing, not to be the enforcement.
const PLATE_PATTERNS = [
  /^[A-Z]\d{3}[A-Z]{3}$/,
  /^[A-Z]{2}\d{2}$/,
  /^[A-Z]{3}\d{2}$/,
];
const PLATE_FORMAT_HELP = tr('Use A123ABC, AB12 or ABC12 — capital letters only');

// Same normalisation the duplicate check uses, so a plate cannot be valid for
// one rule and a different string to the other.
const normalizePlate = normalizeKey;

const isValidPlate = (v) => {
  const cleaned = normalizePlate(v);
  return cleaned !== '' && PLATE_PATTERNS.some((p) => p.test(cleaned));
};

// Flagged only once something has been typed — an empty field is already
// reported as "missing", and showing a format error on a box nobody has
// touched yet is noise.
const plateFormatError = (v) =>
  String(v ?? '').trim() !== '' && !isValidPlate(v);

// Cleans a plate field AS IT IS TYPED: uppercased, spaces and dashes dropped
// on the spot, so the box can only ever hold the canonical form. Paste
// "t 324-dls" and it becomes T324DLS immediately.
//
// The caret is restored by hand, which is the whole reason this is not a
// one-line onChange. React re-renders the input with a SHORTER value than
// the browser just put there, and the caret then lands at the end of the
// string — so correcting a character in the middle of a plate would throw
// you to the end on every keystroke. The fix is to measure how much text
// survives cleaning BEFORE the caret and put it back there: typing at the
// end behaves normally, and editing mid-plate stays where you are.
const sanitizePlateInput = (event, apply) => {
  const el = event.target;
  const raw = el.value;
  const caret = typeof el.selectionStart === 'number' ? el.selectionStart : raw.length;

  const cleaned = raw.toUpperCase().replace(/[^A-Z0-9]/g, '');
  const keptBeforeCaret = raw.slice(0, caret).toUpperCase().replace(/[^A-Z0-9]/g, '').length;

  apply(cleaned);

  // After React has committed the new value, not before — setting it now
  // would be overwritten by the re-render.
  requestAnimationFrame(() => {
    try {
      el.setSelectionRange(keptBeforeCaret, keptBeforeCaret);
    } catch (e) {
      /* detached input, or a type that has no selection range — harmless */
    }
  });
};

const MAX_BULK_ROWS = 50;

// ─── Create one or many orders ────────────────────────────────────────────
// Bulk by design: you set how many orders you need, fill a row per order,
// and submit once. A single order is just this with rowCount = 1, so there
// is no separate single-order path to keep in sync.
const OrderModal = ({ show, onHide, stations, getToken, onSaved, stationName, existingOrders = [] }) => {
  const [rows, setRows] = useState([emptyRow()]);
  const [stationSerials, setStationSerials] = useState([]);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(null); // { done, total } while saving
  // Switches from the form to the results view once submitted — the point of
  // an order is to hand over something scannable immediately.
  const [results, setResults] = useState(null);   // [{ ok, row, card?, error? }]
  const [qrMap, setQrMap] = useState({});         // card_id -> data URL

  // A manager bound to ONE station has nothing to choose, so the picker is
  // hidden and that station is selected automatically. `stations` is already
  // scoped by role upstream (scopeStations), so its length is the whole
  // question — no need to re-derive the user's role here.
  const singleStation = stations.length === 1;
  const isPhone = useIsPhone();

  // `${field}-${rowIndex}` -> the DOM input, so a validation dialog can send
  // the operator straight to the box that needs fixing. Without this, a
  // "Row 7 — bad plate" message on a 20-row bulk form leaves them counting
  // rows by eye to find it.
  const fieldRefs = React.useRef({});
  const setFieldRef = (key) => (el) => {
    if (el) fieldRefs.current[key] = el;
    else delete fieldRefs.current[key];
  };

  const focusField = (key) => {
    const el = fieldRefs.current[key];
    if (!el) return;
    // A tick after the dialog closes — focusing while SweetAlert still owns
    // the focus trap gets undone the moment it tears down.
    setTimeout(() => {
      try {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        el.focus();
        if (typeof el.select === 'function') el.select();
      } catch (e) { /* not focusable any more — nothing to do */ }
    }, 0);
  };

  // Every validation dialog offers the same two choices: fix it now (focus
  // the offending field) or dismiss. Kept in one place so they cannot drift
  // into saying different things.
  const problemDialog = async (opts, focusKey) => {
    const res = await Swal.fire({
      showCancelButton: true,
      confirmButtonText: tr("Edit"),
      cancelButtonText: tr("Close"),
      ...opts,
    });
    if (res.isConfirmed && focusKey) focusField(focusKey);
  };

  // Live duplicate feedback while typing, against the list the page already
  // has. This is a CONVENIENCE only — it can be stale (another operator may
  // have created an order a minute ago) and it only covers whatever the page
  // last fetched, which is narrowed by the station filter. The decision is
  // made in submit() against a freshly fetched list; this just means the
  // operator usually sees the problem before pressing the button.
  // Only orders that share a station with the ones being created can clash,
  // so the index is built from that subset. Rebuilt when the station choice
  // changes — picking a different station genuinely changes what counts as a
  // duplicate, and a stale index would keep flagging the old one.
  const existingIndex = React.useMemo(
    () => buildOrderIndex((existingOrders || []).filter((o) => sharesStation(o, stationSerials))),
    [existingOrders, stationSerials]
  );
  const liveConflicts = React.useMemo(
    () => findConflicts(rows, existingIndex),
    [rows, existingIndex]
  );
  const conflictFor = (rowIndex, key) =>
    liveConflicts.find((c) => c.rowNo === rowIndex + 1 && c.key === key);

  // Which of the selected stations an existing order also covers — that
  // overlap is the reason it clashes, so it is what the operator is shown.
  const clashStations = (order) =>
    toSerialArray(order.station_serial)
      .filter((s) => stationSerials.includes(s))
      .map((s) => stationName(s));

  // One line of red under a field, naming what it clashes with — the order id
  // and station if it is an existing order, or the row number inside this
  // same batch.
  const conflictNote = (rowIndex, key) => {
    const c = conflictFor(rowIndex, key);
    if (!c) return null;
    const where = c.order ? clashStations(c.order).join(', ') : '';
    return (
      <div className="text-danger" style={{ fontSize: '0.8em', marginTop: '0.2rem' }}>
        {c.againstKind === 'order'
          ? <>{tr("Open order")} <b>{c.against}</b>{where ? tr(" at {0}", where) : ''}</>
          : <>{tr("Same as {0}", c.against)}</>}
      </div>
    );
  };

  useEffect(() => {
    if (show) {
      setRows([emptyRow()]);
      setResults(null);
      setQrMap({});
      setProgress(null);
      setStationSerials(singleStation ? [stations[0].serial_number] : []);
    }
  }, [show, stations, singleStation]);

  const setRowCount = (value) => {
    const count = Math.max(1, Math.min(MAX_BULK_ROWS, Number(value) || 1));
    setRows((prev) => {
      // Existing rows are preserved when growing or shrinking, so adjusting
      // the count after typing does not wipe what is already filled in.
      const next = prev.slice(0, count);
      while (next.length < count) next.push(emptyRow());
      return next;
    });
  };

  const updateRow = (index, field, value) =>
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));

  const removeRow = (index) =>
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));

  const renderQR = (id) =>
    QRCode.toDataURL(id, {
      width: 300,
      margin: 2,
      color: { dark: '#000000', light: '#FFFFFF' },
    });

  // Prints every successfully created order, one per page.
  const printAll = () => {
    const printable = (results || []).filter((r) => r.ok && qrMap[r.card.card_id]);
    if (printable.length === 0) return;

    const pages = printable.map((r) => {
      const c = r.card;
      // The TIN carries a prefix because a plate and a TIN are both short
      // alphanumeric strings — on a slip handed to a driver, "T750Dxa ·
      // 123456789" gives no clue which is which.
      const meta = [
        tr('{0} litres', Number(c.preset_value).toLocaleString()),
        c.product || tr('Diesel or Unleaded'),
        c.license_plate || '',
        c.tin_number ? ` ${c.tin_number}` : '',
        c.customer_name || '',
      ].filter(Boolean).join(' · ');
      return `
        <div class="page">
          <div class="qr-container">
            <img src="${qrMap[c.card_id]}" alt="QR Code" class="qr-image" />
          </div>
          <div class="qr-id">${c.card_id}</div>
          <div class="qr-meta">${meta}</div>
        </div>`;
    }).join('');

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Print Orders</title>
          <style>
            /* Sized to content, not a full sheet - these print on a small
               wireless/portable terminal printer, so a min-height:100vh page
               with decorative framing prints mostly blank paper. Each .page
               is just tall enough for the QR + its caption. */
            @page { margin: 0; size: auto; }
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { background: white; font-family: sans-serif; }
            .page {
              display: flex; flex-direction: column; justify-content: flex-start;
              align-items: center; width: fit-content; padding: 10px 14px;
              page-break-after: always;
            }
            .page:last-child { page-break-after: auto; }
            .qr-container {
              display: flex; justify-content: center; align-items: center;
              background: white;
            }
            .qr-image { display: block; width: 220px; height: 220px; }
            .qr-id { margin-top: 8px; font-size: 15px; font-weight: 700; letter-spacing: 0.05em; }
            .qr-meta { margin-top: 3px; font-size: 12px; color: #444; text-align: center; }
            @media print {
              @page { margin: 0; }
              body { margin: 0; padding: 0; }
              .page { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          ${pages}
          <script>
            window.onload = function() {
              setTimeout(function() { window.print(); }, 350);
              window.onafterprint = function() { window.close(); };
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const submit = async (e) => {
    e.preventDefault();

    if (stationSerials.length === 0) {
      Swal.fire({ icon: 'warning', title: tr("Choose a station"), text: tr("At least one station is required.") });
      return;
    }

    // Completeness is checked BEFORE the numeric check, so a row with an
    // empty amount is reported as "missing Amount" rather than as a bad
    // number — the operator is told what to type, not what is wrong.
    for (let i = 0; i < rows.length; i += 1) {
      const missing = missingFields(rows[i], ORDER_FIELDS);
      if (missing.length > 0) {
        // The first missing field is where Edit sends them — fixing it in
        // order is the fastest way through a row with several gaps.
        const firstMissingKey = ORDER_FIELDS
          .filter(([key]) => !String(rows[i][key] ?? '').trim())
          .map(([key]) => key)[0];
        await problemDialog({
          icon: 'warning',
          title: tr("Row {0} is incomplete", i + 1),
          html: `${tr("Still needed: <b>{0}</b>", missing.join(', '))}`,
        }, `${firstMissingKey}-${i}`);
        return;
      }
    }

    const badIndex = rows.findIndex((r) => Number(r.amount) <= 0);
    if (badIndex !== -1) {
      await problemDialog({
        icon: 'warning',
        title: tr("Check the amounts"),
        text: tr("Row {0} needs an amount greater than 0.", badIndex + 1),
      }, `amount-${badIndex}`);
      return;
    }

    // Plate format. Checked before the duplicate lookup below, because a
    // malformed plate would be compared against nothing meaningful anyway —
    // and because the server rejects it, so finding out here saves a round
    // trip per bad row.
    const badPlates = rows
      .map((r, i) => ({ rowNo: i + 1, value: String(r.license_plate).trim() }))
      .filter((r) => !isValidPlate(r.value));
    if (badPlates.length > 0) {
      await problemDialog({
        icon: 'warning',
        title: badPlates.length === 1 ? tr("Check the plate") : tr("Check the plates"),
        html:
          `<p style="margin-bottom:.5rem">${PLATE_FORMAT_HELP}.</p>`
          + `<ul style="text-align:left;display:inline-block;margin:0">`
          + badPlates.map((r) => `<li>${tr('<b>Row {0}</b> — {1}', r.rowNo, r.value || tr('(empty)'))}</li>`).join('')
          + `</ul>`,
      }, `license_plate-${badPlates[0].rowNo - 1}`);
      return;
    }

    // ── Duplicate plate / TIN, per station ───────────────────────────────
    // Checked against a FRESHLY fetched list, not `existingOrders`. That prop
    // is whatever the page last loaded, and it is narrowed by the station
    // filter — so creating an order for station A while the page is filtered
    // to station B would miss a duplicate entirely. It can also be minutes
    // old. This one extra GET is the difference between a real rule and a
    // hint.
    //
    // /all is fetched and then narrowed HERE, rather than querying per
    // station: an order can list several stations, and the per-station
    // endpoint would have to be called once per selected serial and unioned,
    // which is more round trips for the same answer.
    let liveOrders = null;
    try {
      const res = await fetch(`${API_BASE}/ak4tek/cards/all`, {
        headers: { Authorization: getToken() },
      });
      const j = await res.json();
      if (!res.ok || !Array.isArray(j.data)) throw new Error(`Request failed (${res.status})`);
      liveOrders = j.data.filter((c) => c.is_order || isOrderCardId(c.card_id));
    } catch (err) {
      // The check could not run. Creating blindly might duplicate a plate;
      // refusing outright would block all work whenever the API hiccups.
      // The operator decides, with the risk stated plainly.
      const proceed = await Swal.fire({
        icon: 'warning',
        title: tr("Could not check for duplicates"),
        text:
          tr("The existing orders could not be loaded, so a duplicate plate or TIN cannot be ruled out. Create these orders anyway?"),
        showCancelButton: true,
        confirmButtonText: tr("Create anyway"),
        cancelButtonText: tr("Cancel"),
        confirmButtonColor: '#d33',
      });
      if (!proceed.isConfirmed) return;
    }

    if (liveOrders) {
      // Narrowed to orders sharing a station with this batch. An order for
      // the same plate at a DIFFERENT station is not a duplicate and must
      // not block anything.
      const sameStation = liveOrders.filter((o) => sharesStation(o, stationSerials));
      const conflicts = findConflicts(rows, buildOrderIndex(sameStation));

      if (conflicts.length > 0) {
        const first = conflicts[0];
        await problemDialog({
          icon: 'error',
          title: conflicts.length === 1 ? tr("Duplicate found") : tr("{0} duplicates found", conflicts.length),
          html:
            `<p style="margin-bottom:.5rem">${tr("An open order already exists at this station. Each one has to be used at the pump before a new order can be created with the same plate or TIN there.")}</p><ul style="text-align:left;display:inline-block;margin:0">${conflicts.map((c) => {
                const where = c.order ? clashStations(c.order).join(', ') : '';
                return `<li>${tr('<b>Row {0}</b> — {1} <b>{2}</b>', c.rowNo, c.label, c.value)} `
                  + (c.againstKind === 'order'
                      ? tr('is already on order <b>{0}</b>', c.against) + (where ? tr(' at {0}', where) : '')
                      : tr('is the same as {0} in this form', c.against))
                  + `</li>`;
              }).join('')}</ul>`,
        }, `${first.key}-${first.rowNo - 1}`);
        return;
      }
    }

    setSaving(true);
    setProgress({ done: 0, total: rows.length });

    // Sent ONE AT A TIME, not in parallel. The backend creates a single order
    // per POST, and firing 50 concurrent requests at it is exactly the kind of
    // burst that exhausts the server's connection pool. Sequential is slower
    // but predictable, and it means a failure part-way through leaves a clear
    // record of what did and did not get created.
    const out = [];
    for (let i = 0; i < rows.length; i += 1) {
      const row = rows[i];
      try {
        const res = await fetch(`${API_BASE}/ak4tek/cards/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: getToken() },
          body: JSON.stringify({
            // Fixed for every order — never editable from the form.
            is_order: true,
            payment_method: 'credit',
            preset_type: 'volume',
            is_customer: 1,
            amount: Number(row.amount),
            product: row.product,
            // Sent in canonical form, so the stored spelling is the same
            // whichever way the operator typed it.
            license_plate: normalizePlate(row.license_plate),
            tin_number: row.tin_number.trim(),
            customer_name: row.customer_name.trim(),
            station_serial: stationSerials,
          }),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json.message || `Request failed (${res.status})`);
        out.push({
          ok: true,
          row: i + 1,
          card: json.data,
          provisioning: json.provisioning || [],
        });
      } catch (err) {
        out.push({ ok: false, row: i + 1, error: err.message, data: row });
      }
      setProgress({ done: i + 1, total: rows.length });
    }

    setSaving(false);
    setResults(out);
    onSaved();

    // QRs are generated after the fact so a slow render never delays the
    // create calls themselves.
    const map = {};
    for (const r of out) {
      if (r.ok) {
        try {
          map[r.card.card_id] = await renderQR(r.card.card_id);
        } catch (err) { /* a missing QR still leaves the id printable */ }
      }
    }
    setQrMap(map);

    // A row that failed outright IS worth interrupting for — nothing was
    // created and the operator has to retry it.
    const failed = out.filter((r) => !r.ok);
    if (failed.length > 0) {
      Swal.fire({
        icon: 'warning',
        title: tr("{0} of {1} orders created", out.length - failed.length, out.length),
        html:
          `${tr("These rows failed:")}<br/><br/>${failed.map((r) => tr('<b>Row {0}</b> — {1}', r.row, r.error)).join('<br/>')}`,
      });
    }
    // Provisioning failures deliberately do NOT raise a dialog any more —
    // they are surfaced as a badge on the affected card in the results view
    // below instead. Still shown, because the order exists in the cloud but
    // never reached the station, so its QR will not work at the pump; a
    // silently-handed-out dead QR is worse than a quiet warning.
  };

  const okResults = (results || []).filter((r) => r.ok);

  return (
    <Modal show={show} onHide={onHide} size="xl" backdrop="static" className="orders-modal">
      <Modal.Header closeButton>
        <Modal.Title>
          {results
            ? tr("Orders Ready — {0} of {1}", okResults.length, results.length)
            : tr("Create Fuel Orders")}
        </Modal.Title>
      </Modal.Header>

      {results ? (
        <>
          <Modal.Body>
            <div className="order-results-grid">
              {results.map((r) => (
                r.ok ? (
                  <div className="order-result-card" key={r.card.card_id}>
                    <div className="order-created-id">{r.card.card_id}</div>
                    {/* Reads the SERVER's echo of the saved card, not the form
                        row — so a field the backend dropped shows as absent
                        here rather than looking saved. If the TIN was filled
                        in the form but is missing from this line, _serialize()
                        in cards.py is what to check. */}
                    <div className="order-created-meta">
                      {tr("{0} litres", Number(r.card.preset_value).toLocaleString())}
                      {r.card.product ? ` · ${r.card.product}` : tr(" · Diesel or Unleaded")}
                      {r.card.license_plate ? ` · ${r.card.license_plate}` : ''}
                      {r.card.tin_number ? ` ·  ${r.card.tin_number}` : ''}
                      {r.card.customer_name ? ` · ${r.card.customer_name}` : ''}
                    </div>
                    {(r.provisioning || []).some((p) => !p.ok) && (
                      <div
                        className="order-not-provisioned"
                        title={r.provisioning.filter((p) => !p.ok).map((p) => p.message).join(', ')}
                      >
                        <i className="fas fa-triangle-exclamation" /> {tr("Not sent to station")}
                      </div>
                    )}
                    {qrMap[r.card.card_id] && (
                      <div className="qr-image-wrap">
                        <img
                          src={qrMap[r.card.card_id]}
                          alt={tr("Order QR Code")}
                          style={{ maxWidth: '180px' }}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="order-result-card order-result-failed" key={`fail-${r.row}`}>
                    <div className="order-created-id">{tr("Row {0} failed", r.row)}</div>
                    <div className="order-created-meta">{r.error}</div>
                  </div>
                )
              ))}
            </div>
            <p className="qr-panel-desc" style={{ marginTop: '0.75rem', textAlign: 'center' }}>
              {tr("Scan at the pump, or print to hand over. Each order is good for one use and cannot be recharged.")}
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={printAll}
              disabled={okResults.length === 0}
            >
              <i className="fas fa-print" /> {tr("Print")} {okResults.length > 1 ? tr("All ({0})", okResults.length) : ''}
            </Button>
            <Button variant="primary" onClick={onHide}>
              {tr("Done")}
            </Button>
          </Modal.Footer>
        </>
      ) : (
        <Form onSubmit={submit}>
          <Modal.Body>
            <Row className="align-items-end mb-3">
              <Col md={3}>
                <Form.Group className="mb-0">
                  <Form.Label>{tr("How many orders?")}</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    max={MAX_BULK_ROWS}
                    value={rows.length}
                    onChange={(e) => setRowCount(e.target.value)}
                    autoFocus
                  />
                </Form.Group>
              </Col>
              <Col md={9}>
                {singleStation ? (
                  // Nothing to choose — shown as plain text so it is still
                  // obvious which station these orders belong to.
                  <div className="order-single-station">
                    <i className="fas fa-gas-pump" /> {stations[0].name}
                    <span className="order-single-station-serial">
                      {stations[0].serial_number}
                    </span>
                  </div>
                ) : (
                  <SerialField
                    serials={stationSerials}
                    setSerials={setStationSerials}
                    stations={stations}
                    stationName={stationName}
                  />
                )}
              </Col>
            </Row>

            {isPhone ? (
              // ── Phone: one vertical card per order row, fields stacked -
              // no table, nothing to scroll sideways for. Same data/handlers
              // as the desktop table below, just laid out differently.
              <div className="order-bulk-card-list">
                {rows.map((row, i) => (
                  <div className="order-bulk-card" key={i}>
                    <div className="order-bulk-card-header">
                      <span>{tr("Order #{0}", i + 1)}</span>
                      <Button
                        variant="link"
                        size="sm"
                        className="order-bulk-remove"
                        onClick={() => removeRow(i)}
                        disabled={rows.length <= 1}
                        title={tr("Remove this row")}
                        type="button"
                      >
                        ×
                      </Button>
                    </div>
                    <Form.Group className="mb-2">
                      <Form.Label>{tr("Amount (Litres) *")}</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        value={row.amount}
                        onChange={(e) => updateRow(i, 'amount', e.target.value)}
                        required
                      />
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label>{tr("Product *")}</Form.Label>
                      <Form.Control
                        as="select"
                        value={row.product}
                        onChange={(e) => updateRow(i, 'product', e.target.value)}
                        required
                      >
                        <option value="">{tr("Select product…")}</option>
                        <option value="DIESEL">{tr("Diesel only")}</option>
                        <option value="UNLEADED">{tr("Unleaded only")}</option>
                      </Form.Control>
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label>{tr("License Plate *")}</Form.Label>
                      {/* Uppercased as it is typed rather than silently on
                          submit, so what is on screen is what gets stored.
                          Separators are left alone while typing — yanking a
                          space out from under the cursor is maddening — and
                          removed on submit. */}
                      <Form.Control
                        type="text"
                        value={row.license_plate}
                        onChange={(e) => updateRow(i, 'license_plate', e.target.value.toUpperCase())}
                        placeholder={tr("e.g. T324DLS")}
                        isInvalid={!!conflictFor(i, 'license_plate') || plateFormatError(row.license_plate)}
                        required
                      />
                      {plateFormatError(row.license_plate) && (
                        <div className="text-danger" style={{ fontSize: '0.8em', marginTop: '0.2rem' }}>
                          {PLATE_FORMAT_HELP}
                        </div>
                      )}
                      {conflictNote(i, 'license_plate')}
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label>{tr("TIN Number *")}</Form.Label>
                      <Form.Control
                        type="text"
                        value={row.tin_number}
                        onChange={(e) => updateRow(i, 'tin_number', e.target.value)}
                        isInvalid={!!conflictFor(i, 'tin_number')}
                        required
                      />
                      {conflictNote(i, 'tin_number')}
                    </Form.Group>
                    <Form.Group className="mb-0">
                      <Form.Label>{tr("Driver Name *")}</Form.Label>
                      <Form.Control
                        type="text"
                        value={row.customer_name}
                        onChange={(e) => updateRow(i, 'customer_name', e.target.value)}
                        required
                      />
                    </Form.Group>
                  </div>
                ))}
              </div>
            ) : (
              // ── Desktop/tablet: original compact table, one row per order.
              <div className="order-bulk-table-wrap">
                <table className="table table-sm order-bulk-table">
                  <thead>
                    <tr>
                      <th style={{ width: '3rem' }}>#</th>
                      <th style={{ width: '9rem' }}>{tr("Amount (Litres) *")}</th>
                      <th style={{ width: '12rem' }}>{tr("Product *")}</th>
                      <th>{tr("License Plate *")}</th>
                      <th>{tr("TIN Number *")}</th>
                      <th>{tr("Driver Name *")}</th>
                      <th style={{ width: '3rem' }} />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => (
                      <tr key={i}>
                        <td className="order-bulk-rownum">{i + 1}</td>
                        <td>
                          <Form.Control
                            type="number"
                            min="0"
                            value={row.amount}
                            onChange={(e) => updateRow(i, 'amount', e.target.value)}
                            required
                          />
                        </td>
                        <td>
                          <Form.Control
                            as="select"
                            value={row.product}
                            onChange={(e) => updateRow(i, 'product', e.target.value)}
                            required
                          >
                            <option value="">{tr("Select product…")}</option>
                            <option value="DIESEL">{tr("Diesel only")}</option>
                            <option value="UNLEADED">{tr("Unleaded only")}</option>
                          </Form.Control>
                        </td>
                        <td>
                          <Form.Control
                            type="text"
                            value={row.license_plate}
                            onChange={(e) => updateRow(i, 'license_plate', e.target.value.toUpperCase())}
                            placeholder={tr("e.g. T324DLS")}
                            isInvalid={!!conflictFor(i, 'license_plate') || plateFormatError(row.license_plate)}
                            required
                          />
                          {plateFormatError(row.license_plate) && (
                            <div className="text-danger" style={{ fontSize: '0.8em', marginTop: '0.2rem' }}>
                              {PLATE_FORMAT_HELP}
                            </div>
                          )}
                          {conflictNote(i, 'license_plate')}
                        </td>
                        <td>
                          <Form.Control
                            type="text"
                            value={row.tin_number}
                            onChange={(e) => updateRow(i, 'tin_number', e.target.value)}
                            isInvalid={!!conflictFor(i, 'tin_number')}
                            required
                          />
                          {conflictNote(i, 'tin_number')}
                        </td>
                        <td>
                          <Form.Control
                            type="text"
                            value={row.customer_name}
                            onChange={(e) => updateRow(i, 'customer_name', e.target.value)}
                            required
                          />
                        </td>
                        <td>
                          <Button
                            variant="link"
                            size="sm"
                            className="order-bulk-remove"
                            onClick={() => removeRow(i)}
                            disabled={rows.length <= 1}
                            title={tr("Remove this row")}
                            type="button"
                          >
                            ×
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <Button
              variant="outline-secondary"
              size="sm"
              type="button"
              onClick={() => setRowCount(rows.length + 1)}
              disabled={rows.length >= MAX_BULK_ROWS}
            >
              {tr("+ Add Row")}
            </Button>

            <div className="order-locked-notice">
              <i className="fas fa-info-circle" /> {tr("Every field is required. A plate or TIN that already has an open order")} <b>{tr("at the same station")}</b> {tr("cannot be given another one until that order is used at the pump. A QR code is generated for each order once saved. Every order is fixed at its amount and cannot be recharged.")}{' '}
              <b>{tr("One use only")}</b> {tr("— the first fill ends the order even if litres are left on it, and any order not used within")}{' '}
              <b>{tr("24 hours")}</b> {tr("is removed automatically.")}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={onHide} disabled={saving}>
              {tr("Cancel")}
            </Button>
            <Button variant="primary" type="submit" disabled={saving}>
              {saving && progress
                ? tr("Creating {0} / {1}…", progress.done, progress.total)
                : tr("Create {0}", rows.length > 1 ? tr("{0} Orders", rows.length) : tr("Order"))}
            </Button>
          </Modal.Footer>
        </Form>
      )}
    </Modal>
  );
};

// ─── Edit an existing order ────────────────────────────────────────────────
// Deliberately lighter than Cards.js's CardFormFields: an order only has a
// few legitimately editable fields. Amount/credit are NOT here at all —
// cards.py rejects any attempt to change preset_value on an order outright,
// and credit only ever moves down (spending), never through this admin form.
//
// Every field here is required, same as the create form — otherwise a
// complete order could be created and then have its plate blanked out by an
// edit, and the rule would hold only on the way in.
//
// ! Orders created BEFORE this rule may have an empty product or TIN. Opening
// one of those here will refuse to save until the gap is filled. That is
// deliberate — it is the only prompt anyone gets to backfill them — but it
// does mean an old order cannot be edited without completing it first.
const OrderEditModal = ({ show, onHide, order, stations, getToken, onSaved, stationName }) => {
  const [formData, setFormData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (show && order) {
      setFormData({
        customer_name: order.customer_name || '',
        product: order.product || '',
        license_plate: order.license_plate || '',
        tin_number: order.tin_number || '',
        station_serial: toSerialArray(order.station_serial),
      });
    }
  }, [show, order]);

  if (!formData) return null;

  const onChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const setSerials = (arr) =>
    setFormData((prev) => ({ ...prev, station_serial: arr }));

  const submit = async (e) => {
    e.preventDefault();
    if (formData.station_serial.length === 0) {
      Swal.fire({ icon: 'warning', title: tr("Choose a station"), text: tr("At least one station is required.") });
      return;
    }

    const missing = missingFields(formData, EDIT_FIELDS);
    if (missing.length > 0) {
      Swal.fire({
        icon: 'warning',
        title: tr("Incomplete order"),
        html: `${tr("Still needed: <b>{0}</b>", missing.join(', '))}`,
      });
      return;
    }

    // The same format rule as creation. Without it an edit would be the way
    // around the standard — and the server would reject the save anyway.
    if (!isValidPlate(formData.license_plate)) {
      Swal.fire({
        icon: 'warning',
        title: tr("Check the plate"),
        text: PLATE_FORMAT_HELP,
      });
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(
        `${API_BASE}/ak4tek/cards/${encodeURIComponent(order.card_id)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: getToken() },
          body: JSON.stringify({
            customer_name: formData.customer_name.trim(),
            product: formData.product,
            license_plate: normalizePlate(formData.license_plate),
            tin_number: formData.tin_number.trim(),
            station_serial: formData.station_serial,
          }),
        }
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(json.message || `Request failed (${res.status})`);
      }
      Swal.fire({
        icon: 'success',
        title: tr("Updated"),
        timer: 1200,
        showConfirmButton: false,
      });
      onSaved();
      onHide();
    } catch (e) {
      Swal.fire({ icon: 'error', title: tr("Could not update order"), text: e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static" className="orders-modal">
      <Modal.Header closeButton>
        <Modal.Title>{tr("Edit Order — {0}", order?.card_id)}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={submit}>
        <Modal.Body>
          <div className="order-locked-notice">
            <i className="fas fa-lock" /> {tr("Amount ({0}", Number(order?.preset_value).toLocaleString())}{' '}
            {order?.preset_type === 'volume' ? tr("litres") : tr("TSH")}{tr(") and remaining balance are fixed and cannot be changed here.")}
          </div>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{tr("Product *")}</Form.Label>
                <Form.Control
                  as="select"
                  name="product"
                  value={formData.product}
                  onChange={onChange}
                  required
                >
                  <option value="">{tr("Select product…")}</option>
                  <option value="DIESEL">{tr("Diesel only")}</option>
                  <option value="UNLEADED">{tr("Unleaded only")}</option>
                </Form.Control>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{tr("License Plate *")}</Form.Label>
                <Form.Control
                  type="text"
                  name="license_plate"
                  value={formData.license_plate}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      license_plate: e.target.value.toUpperCase(),
                    }))
                  }
                  isInvalid={plateFormatError(formData.license_plate)}
                  required
                />
                <Form.Text className={plateFormatError(formData.license_plate) ? 'text-danger' : 'text-muted'}>
                  {PLATE_FORMAT_HELP}
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{tr("TIN Number *")}</Form.Label>
                <Form.Control
                  type="text"
                  name="tin_number"
                  value={formData.tin_number}
                  onChange={onChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{tr("Driver Name *")}</Form.Label>
                <Form.Control
                  type="text"
                  name="customer_name"
                  value={formData.customer_name}
                  onChange={onChange}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Hidden when the user is bound to a single station — there is
              nothing to choose, and the order already belongs to it. */}
          {stations.length === 1 ? (
            <div className="order-single-station">
              <i className="fas fa-gas-pump" /> {stations[0].name}
              <span className="order-single-station-serial">
                {stations[0].serial_number}
              </span>
            </div>
          ) : (
            <SerialField
              serials={formData.station_serial}
              setSerials={setSerials}
              stations={stations}
              stationName={stationName}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={saving}>
            {tr("Cancel")}
          </Button>
          <Button variant="primary" type="submit" disabled={saving}>
            {saving ? tr("Saving…") : tr("Save")}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

// ─── Order detail (phone view) ─────────────────────────────────────────────
// On a phone the table is replaced by a plain vertical list of cards (see
// isPhone below) — no horizontal scrolling at all. Tapping a card opens
// this instead: everything the table's row used to show, laid out as a
// simple vertical sheet, plus the same three actions the table row had
// (QR / Edit / Delete) as full-width buttons instead of cramped icons.
const OrderDetailModal = ({ show, onHide, order, stationName, onShowQr, onEdit, onDelete }) => {
  if (!order) return null;

  return (
    <Modal show={show} onHide={onHide} centered className="orders-modal">
      <Modal.Header closeButton>
        <Modal.Title>{order.card_id}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="order-detail-rows">
          <div className="order-detail-row">
            <span className="order-detail-label">{tr("Driver")}</span>
            <span className="order-detail-value">
              {order.customer_name || <span className="text-muted">—</span>}
            </span>
          </div>
          <div className="order-detail-row">
            <span className="order-detail-label">{tr("Product")}</span>
            <span className="order-detail-value">
              {order.product || tr("Either (Diesel or Unleaded)")}
            </span>
          </div>
          <div className="order-detail-row">
            <span className="order-detail-label">{tr("License Plate")}</span>
            <span className="order-detail-value">
              {order.license_plate || <span className="text-muted">—</span>}
            </span>
          </div>
          <div className="order-detail-row">
            <span className="order-detail-label">{tr("TIN")}</span>
            <span className="order-detail-value">
              {order.tin_number || <span className="text-muted">—</span>}
            </span>
          </div>
          <div className="order-detail-row">
            <span className="order-detail-label">{tr("Station")}</span>
            <span className="order-detail-value">
              <div className="d-flex flex-wrap" style={{ gap: '0.3rem', justifyContent: 'flex-end' }}>
                {toSerialArray(order.station_serial).map((s) => (
                  <span key={s} className="badge bg-light text-dark border" title={s}>
                    {stationName(s)}
                  </span>
                ))}
              </div>
            </span>
          </div>
          <div className="order-detail-row">
            <span className="order-detail-label">{trc('litres', 'Amount')}</span>
            <span className="order-detail-value">
              {Number(order.preset_value).toLocaleString()}{' '}
              <small className="text-muted">
                {order.preset_type === 'volume' ? tr("L") : tr("TSH")}
              </small>
            </span>
          </div>
          <div className="order-detail-row">
            <span className="order-detail-label">{tr("Remaining")}</span>
            <span
              className={
                'order-detail-value ' +
                (Number(order.credit) <= 0 ? 'order-remaining-empty' : 'order-remaining')
              }
            >
              {Number(order.credit || 0).toLocaleString()}
            </span>
          </div>
          <div className="order-detail-row">
            <span className="order-detail-label">{tr("Created")}</span>
            <span className="order-detail-value">
              {orderTime(order.created_at)}
            </span>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="order-detail-actions">
        <Button variant="info" onClick={() => onShowQr(order)}>
          <i className="fas fa-qrcode" /> {tr("QR")}
        </Button>
        <Button variant="warning" onClick={() => onEdit(order)}>
          <i className="fas fa-edit" /> {tr("Edit")}
        </Button>
        <Button variant="danger" onClick={() => onDelete(order)}>
          <i className="fas fa-trash" /> {tr("Delete")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// ─── View / re-print an existing order's QR ───────────────────────────────
// The QR only appears once, right after creation, inside OrderModal's
// success view — closing that modal loses it. This is the same
// render/print logic, just reachable from the table for ANY order at any
// time (e.g. the printer jammed, or the customer lost the first copy). The
// QR is nothing more than an encoding of card_id, so there is nothing to
// regenerate server-side — re-encoding the existing id is enough.
//
// Shows the SAME fields as OrderDetailModal, reusing its .order-detail-*
// classes. Previously only the amount and remaining balance were here, which
// made the modal useless for the one job it is usually opened for: checking
// you are about to hand the right slip to the right driver. A QR is opaque —
// nothing on screen distinguished one order from another.
const OrderQrModal = ({ show, onHide, order, stationName }) => {
  const [qrCodeData, setQrCodeData] = useState('');

  useEffect(() => {
    if (show && order?.card_id) {
      QRCode.toDataURL(order.card_id, {
        width: 300,
        margin: 2,
        color: { dark: '#000000', light: '#FFFFFF' },
      }).then(setQrCodeData);
    } else {
      setQrCodeData('');
    }
  }, [show, order]);

  const printQRCode = () => {
    if (!qrCodeData) return;

    // Same caption printAll() puts on a freshly created order, so a slip
    // re-printed from the table is indistinguishable from the original —
    // otherwise a reprint would come out blank apart from the id, and the
    // pump attendant would have nothing to check the vehicle against.
    const meta = order
      ? [
          `${Number(order.preset_value).toLocaleString()} ${order.preset_type === 'volume' ? tr('litres') : tr('TSH')}`,
          order.product || tr('Diesel or Unleaded'),
          order.license_plate || '',
          order.tin_number ? ` ${order.tin_number}` : '',
          order.customer_name || '',
        ].filter(Boolean).join(' · ')
      : '';

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Print Order QR</title>
          <style>
            /* Sized to content, not a full sheet - printed on a small
               wireless/portable terminal printer, so a min-height:100vh page
               with a decorative border just wastes paper. Just the QR and
               its caption, as tall as they actually are. */
            @page { margin: 0; size: auto; }
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { display: flex; flex-direction: column; justify-content: flex-start; align-items: center; width: fit-content; padding: 10px 14px; background: white; font-family: sans-serif; }
            .qr-container {
              display: flex; justify-content: center; align-items: center;
              background: white;
            }
            .qr-image { display: block; width: 220px; height: 220px; }
            .qr-id { margin-top: 8px; font-size: 15px; font-weight: 700; letter-spacing: 0.05em; }
            .qr-meta { margin-top: 3px; font-size: 12px; color: #444; text-align: center; }
            @media print {
              @page { margin: 0; }
              body { margin: 0; padding: 0; }
              .qr-container { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="qr-container">
            <img src="${qrCodeData}" alt="QR Code" class="qr-image" />
          </div>
          <div class="qr-id">${order ? order.card_id : ''}</div>
          <div class="qr-meta">${meta}</div>
          <script>
            window.onload = function() {
              setTimeout(function() { window.print(); }, 250);
              window.onafterprint = function() { window.close(); };
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <Modal show={show} onHide={onHide} centered className="orders-modal">
      <Modal.Header closeButton>
        <Modal.Title>{tr("Order QR — {0}", order?.card_id)}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="order-created-summary">
          <div className="order-created-meta" style={{ marginBottom: '0.75rem' }}>
            {order && Number(order.preset_value).toLocaleString()}{' '}
            {order?.preset_type === 'volume' ? tr("litres") : tr("TSH")} {tr("total")}
            {' · '}
            <span className={Number(order?.credit) <= 0 ? 'order-remaining-empty' : 'order-remaining'}>
              {order && Number(order.credit || 0).toLocaleString()} {tr("remaining")}
            </span>
          </div>
          {qrCodeData ? (
            <div className="qr-image-wrap">
              <img src={qrCodeData} alt={tr("Order QR Code")} style={{ maxWidth: '260px' }} />
            </div>
          ) : (
            <div className="text-muted">{tr("Generating…")}</div>
          )}
        </div>

        {/* Everything else about the order, below the QR. Deliberately the
            same markup and classes as OrderDetailModal so the two read
            identically and one stylesheet covers both. */}
        {order && (
          <div className="order-detail-rows" style={{ marginTop: '1rem' }}>
            <div className="order-detail-row">
              <span className="order-detail-label">{tr("Driver")}</span>
              <span className="order-detail-value">
                {order.customer_name || <span className="text-muted">—</span>}
              </span>
            </div>
            <div className="order-detail-row">
              <span className="order-detail-label">{tr("Product")}</span>
              <span className="order-detail-value">
                {order.product || tr("Either (Diesel or Unleaded)")}
              </span>
            </div>
            <div className="order-detail-row">
              <span className="order-detail-label">{tr("License Plate")}</span>
              <span className="order-detail-value">
                {order.license_plate || <span className="text-muted">—</span>}
              </span>
            </div>
            <div className="order-detail-row">
              <span className="order-detail-label">{tr("TIN")}</span>
              <span className="order-detail-value">
                {order.tin_number || <span className="text-muted">—</span>}
              </span>
            </div>
            <div className="order-detail-row">
              <span className="order-detail-label">{tr("Station")}</span>
              <span className="order-detail-value">
                <div className="d-flex flex-wrap" style={{ gap: '0.3rem', justifyContent: 'flex-end' }}>
                  {toSerialArray(order.station_serial).map((s) => (
                    <span key={s} className="badge bg-light text-dark border" title={s}>
                      {/* stationName is optional here — when this modal is
                          opened from somewhere that does not pass it, the
                          raw serial is still better than nothing. */}
                      {stationName ? stationName(s) : s}
                    </span>
                  ))}
                </div>
              </span>
            </div>
            <div className="order-detail-row">
              <span className="order-detail-label">{tr("Created")}</span>
              <span className="order-detail-value">{orderTime(order.created_at)}</span>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={printQRCode} disabled={!qrCodeData}>
          <i className="fas fa-print" /> {tr("Print")}
        </Button>
        <Button variant="primary" onClick={onHide}>
          {tr("Close")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// ─── Orders page ────────────────────────────────────────────────────────────
const Orders = ({ token, LogoutTimeout }) => {
  const [orders, setOrders] = useState([]);
  const [stations, setStations] = useState([]);
  const [stationFilter, setStationFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [orderShow, setOrderShow] = useState(false);
  const [editShow, setEditShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [qrShow, setQrShow] = useState(false);
  const [qrOrder, setQrOrder] = useState(null);
  const [detailShow, setDetailShow] = useState(false);
  const [detailOrder, setDetailOrder] = useState(null);
  // Newest order first by default. The API returns rows in whatever order the
  // table hands back, which is effectively oldest-first — useless here, since
  // the order someone just created is the one they are looking for. The
  // Created header still toggles to oldest-first, and any other header still
  // takes over as before.
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  // Below this width, the table is replaced with a vertical card list (see
  // render below) — no horizontal scrolling anywhere on the page, tapping a
  // card opens OrderDetailModal instead. Shared with OrderModal's bulk-create
  // rows via useIsPhone so both switch layouts at the same width.
  const isPhone = useIsPhone();

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
  };

  // Same /station/all + scopeStations pattern as Cards.js — VFD+ATG only,
  // scoped to the current user's role.
  const fetchStations = useCallback(async () => {
    try {
      const tok = JSON.parse(sessionStorage.getItem('token')).data.access_token;
      const r = await fetch(`${API_BASE}/station/all`, {
        headers: { Authorization: tok },
      });
      const j = await r.json();
      if (j && j.data) {
        const vfdStations = j.data
          .filter((s) => s.is_vfd)
          .map((s) => ({ name: s.name, serial_number: s.serial_number }));
        const scoped = scopeStations(token, vfdStations);
        setStations(scoped);
        // A Manager bound to ONE station has nothing to choose between, so
        // the filter starts on that station instead of "All stations" —
        // same default Cards.js already applies. It matters more here than
        // it looks: the order-period panel below is inert until a single
        // station is selected, so leaving it on "All" would show that
        // account a disabled report with no obvious way to enable it.
        //
        // Keyed off the SCOPED list length, not the role, so it also kicks
        // in for a global account at a deployment that happens to run one
        // VFD station. The dropdown still offers "All stations".
        if (scoped.length === 1) {
          setStationFilter(scoped[0].serial_number);
        }
      }
    } catch (err) {
      console.warn('Could not fetch stations:', err);
    }
  }, [token]);

  // Same cards endpoint Cards.js uses — orders live in the same table, this
  // page just filters down to the ORD- subset instead of excluding it.
  const fetchOrders = useCallback(async (filterSerial) => {
    setLoading(true);
    const tok = authToken();
    if (!tok) {
      setLoading(false);
      return;
    }

    const url = filterSerial
      ? `${API_BASE}/ak4tek/cards/?station_serial=${encodeURIComponent(filterSerial)}`
      : `${API_BASE}/ak4tek/cards/all`;

    // The fetch itself is INSIDE the try, and setLoading(false) is in a
    // finally. It used to sit outside: a rejected fetch (server down, network
    // drop, CORS) skipped straight past setLoading(false), so the page stuck
    // on "Loading…" with Refresh disabled and no way out but a reload —
    // precisely when the API is unwell and you most want to retry.
    try {
      const response = await fetch(url, { headers: { Authorization: tok } });
      const j = await response.json();
      if (response.ok && j && Array.isArray(j.data)) {
        setOrders(j.data.filter((c) => c.is_order || isOrderCardId(c.card_id)));
      } else {
        setOrders([]);
      }
    } catch {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStations();
  }, [fetchStations]);

  useEffect(() => {
    fetchOrders(stationFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationFilter]);

  const handleDelete = async (order) => {
    const result = await Swal.fire({
      title: tr("Delete this order?"),
      html: `<b>${order.card_id}</b><br/>${order.customer_name || ''}<br/>${tr("<small>Remaining: {0}</small>", Number(order.credit || 0).toLocaleString())}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: tr("Yes, delete"),
      cancelButtonText: tr("Cancel"),
      confirmButtonColor: '#d33',
    });
    if (!result.isConfirmed) return;

    try {
      const r = await fetch(
        `${API_BASE}/ak4tek/cards/${encodeURIComponent(order.card_id)}`,
        { method: 'DELETE', headers: { Authorization: authToken() } }
      );
      const j = await r.json().catch(() => ({}));
      if (r.ok) {
        Swal.fire({
          title: tr("Deleted"),
          text: j.message || tr("Order removed."),
          icon: 'success',
          timer: 1500,
          showConfirmButton: false,
        });
        fetchOrders(stationFilter);
      } else {
        Swal.fire({ title: tr("Error {0}", r.status), text: j.message || tr("Delete failed."), icon: 'error' });
      }
    } catch (err) {
      Swal.fire({ title: tr("Connection error"), text: String(err), icon: 'error' });
    }
  };

  const openEdit = (order) => {
    setEditing(order);
    setEditShow(true);
  };

  const openQr = (order) => {
    setQrOrder(order);
    setQrShow(true);
  };

  const openDetail = (order) => {
    setDetailOrder(order);
    setDetailShow(true);
  };

  // The detail modal's own buttons close the detail sheet first, then open
  // the target action — otherwise two modals would be stacked/backdropped
  // on top of each other.
  const openQrFromDetail = (order) => {
    setDetailShow(false);
    openQr(order);
  };
  const openEditFromDetail = (order) => {
    setDetailShow(false);
    openEdit(order);
  };
  const handleDeleteFromDetail = (order) => {
    setDetailShow(false);
    handleDelete(order);
  };

  const stationName = (serial) => {
    const s = stations.find((x) => x.serial_number === serial);
    return s ? s.name : serial;
  };

  // The free-text search box was replaced by the order-period reporting
  // panel, so this sorts `orders` directly — there is no longer a filtered
  // subset in between. An order is found by sorting a column or by picking
  // a station; if per-field search is wanted back, it belongs as its own
  // control rather than squeezed in beside the report.
  const sortedOrders = React.useMemo(() => {
    if (!sortBy) return orders;
    const arr = [...orders];
    arr.sort((a, b) => {
      let av, bv;
      switch (sortBy) {
        case 'card_id':       av = a.card_id || '';               bv = b.card_id || '';               break;
        case 'customer_name': av = a.customer_name || '';         bv = b.customer_name || '';         break;
        case 'product':       av = a.product || '';               bv = b.product || '';               break;
        case 'license_plate': av = a.license_plate || '';         bv = b.license_plate || '';         break;
        case 'tin_number':    av = a.tin_number || '';            bv = b.tin_number || '';            break;
        case 'preset_value':  av = Number(a.preset_value) || 0;   bv = Number(b.preset_value) || 0;   break;
        case 'credit':        av = Number(a.credit) || 0;         bv = Number(b.credit) || 0;         break;
        // Compared as a number, not as text. The stored form
        // ("YYYY-MM-DD HH:mm:ss") happens to sort correctly as a string, but
        // only while every row has the exact same format and width — one row
        // with a 'T' separator or a trailing 'Z' would silently sort wrong.
        // A timestamp has no such trap. An unparseable/missing value becomes 0
        // and lands at the far end rather than scrambling the rest.
        case 'created_at':    av = new Date(a.created_at || 0).getTime() || 0;
                              bv = new Date(b.created_at || 0).getTime() || 0; break;
        default:               av = ''; bv = '';
      }
      if (typeof av === 'string') {
        const cmp = av.localeCompare(bv);
        return sortDir === 'asc' ? cmp : -cmp;
      }
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return arr;
  }, [orders, sortBy, sortDir]);

  const sortHeader = (label, key) => (
    <th onClick={() => handleSort(key)} style={{ cursor: 'pointer', userSelect: 'none' }}>
      {label}{' '}
      {sortBy === key && <i className={`fas fa-sort-${sortDir === 'asc' ? 'up' : 'down'}`} />}
    </th>
  );

  return (
    <div className="OrdersPage">
      <div className="FilterBar">
        <h5 className="FilterBarTitle">{tr("Fuel Orders")}</h5>
        <Row style={{ alignItems: 'flex-end' }}>
          <Col md={6}>
            <Form.Group className="mb-0">
              <Form.Label>{tr("Filter by station")}</Form.Label>
              <Form.Control
                as="select"
                value={stationFilter}
                onChange={(e) => setStationFilter(e.target.value)}
              >
                <option value="">{tr("All stations")}</option>
                {stations.map((s) => (
                  <option key={s.serial_number} value={s.serial_number}>
                    {s.name} ({s.serial_number})
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
          {/* className (not inline style) so the mobile breakpoint in
              Orders.css can stack these full-width and give them a gap —
              an inline style can't be overridden by a later media query. */}
          <Col md={6} className="orders-toolbar-actions">
            <Button variant="success" onClick={() => setOrderShow(true)}>
              <i className="fas fa-qrcode" /> {tr("Create Order")}
            </Button>
            <Button
              variant="outline-secondary"
              onClick={() => fetchOrders(stationFilter)}
              disabled={loading}
            >
              {loading ? tr("Loading…") : tr("Refresh")}
            </Button>
          </Col>
        </Row>

        {/* The order-period reporting panel used to sit here, in place of
            the original Search box. Both are gone: the reporting feature was
            removed along with order_report.py / orders_reports.js. If
            per-field search is wanted back, it belongs as its own control on
            this row rather than squeezed in beside anything else. */}
      </div>

      {isPhone ? (
        // ── Phone: vertical card list, no horizontal scrolling anywhere.
        // Each card shows just enough to identify the order at a glance;
        // tapping it opens the full detail sheet (OrderDetailModal) with
        // every field the table used to show, plus the QR/Edit/Delete
        // actions as full-width buttons.
        <div className="orders-card-list">
          {sortedOrders.length === 0 && !loading && (
            <div className="orders-card-empty">
              <i className="fas fa-qrcode" />
              <div>
                {stationFilter
                  ? tr("No orders for this station.")
                  : tr("No orders yet — create one to get started.")}
              </div>
              {!stationFilter && (
                <Button variant="success" onClick={() => setOrderShow(true)}>
                  <i className="fas fa-qrcode" style={{ marginRight: '0.5em' }} />
                  {tr("Create your first order")}
                </Button>
              )}
            </div>
          )}
          {sortedOrders.map((o) => (
            <div
              key={o.id || o.card_id}
              className="orders-card"
              onClick={() => openDetail(o)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') openDetail(o); }}
            >
              <div className="orders-card-top">
                <span className="card-id">{o.card_id}</span>
                <span
                  className={Number(o.credit) <= 0 ? 'order-remaining-empty' : 'order-remaining'}
                >
                  {tr("{0} left", Number(o.credit || 0).toLocaleString())}
                </span>
              </div>
              {/* The TIN carries a prefix — a plate and a TIN are both short
                  alphanumeric strings, so side by side with no label there is
                  nothing to tell them apart at a glance. */}
              <div className="orders-card-meta">
                {o.customer_name || <span className="text-muted">{tr("No driver name")}</span>}
                {o.license_plate ? ` · ${o.license_plate}` : ''}
                {o.tin_number ? ` ·  ${o.tin_number}` : ''}
              </div>
              <div className="orders-card-meta">
                {Number(o.preset_value).toLocaleString()}{' '}
                {o.preset_type === 'volume' ? tr("L") : tr("TSH")}
                {' · '}
                {o.product || tr("Either")}
              </div>
              <i className="fas fa-chevron-right orders-card-chevron" />
            </div>
          ))}
        </div>
      ) : (
        // ── Desktop/tablet: original sortable table.
        <div className="orders-table-scroll">
          <table className="table table-striped table-bordered table-hover">
            <thead className="thead-dark">
              <tr>
                {sortHeader(tr("Order ID"),   'card_id')}
                {sortHeader(tr("Driver"),     'customer_name')}
                {sortHeader(tr("Product"),    'product')}
                {sortHeader(tr("Plate"),      'license_plate')}
                {sortHeader(tr("TIN"),        'tin_number')}
                <th>{tr("Station")}</th>
                {sortHeader(trc('litres', 'Amount'), 'preset_value')}
                {sortHeader(tr("Remaining"),  'credit')}
                {sortHeader(tr("Created"),    'created_at')}
                <th>{tr("Actions")}</th>
              </tr>
            </thead>
            <tbody>
              {sortedOrders.length === 0 && !loading && (
                <tr>
                  <td colSpan="10" style={{ padding: '3em 1em' }}>
                    <div style={{ textAlign: 'center', color: '#888' }}>
                      <i
                        className="fas fa-qrcode"
                        style={{ fontSize: '3em', color: '#ccc', marginBottom: '0.5em' }}
                      />
                      <div style={{ marginBottom: '1em', fontSize: '1.05em' }}>
                        {stationFilter
                          ? tr("No orders for this station.")
                          : tr("No orders yet — create one to get started.")}
                      </div>
                      {!stationFilter && (
                        <Button variant="success" onClick={() => setOrderShow(true)}>
                          <i className="fas fa-qrcode" style={{ marginRight: '0.5em' }} />
                          {tr("Create your first order")}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              )}
              {sortedOrders.map((o) => (
                <tr key={o.id || o.card_id}>
                  <td><span className="card-id">{o.card_id}</span></td>
                  <td>{o.customer_name}</td>
                  <td>{o.product || <span className="text-muted">{tr("Either")}</span>}</td>
                  <td>{o.license_plate || <span className="text-muted">—</span>}</td>
                  <td>{o.tin_number || <span className="text-muted">—</span>}</td>
                  <td title={toSerialArray(o.station_serial).join(', ')}>
                    <div className="d-flex flex-wrap" style={{ gap: '0.3rem' }}>
                      {toSerialArray(o.station_serial).map((s) => (
                        <span key={s} className="badge bg-light text-dark border" title={s}>
                          {stationName(s)}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {Number(o.preset_value).toLocaleString()}{' '}
                    <small className="text-muted">
                      {o.preset_type === 'volume' ? tr("L") : tr("TSH")}
                    </small>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <span
                      className={Number(o.credit) <= 0 ? 'order-remaining-empty' : 'order-remaining'}
                    >
                      {Number(o.credit || 0).toLocaleString()}
                    </span>
                  </td>
                  <td>
                    <small title={o.created_at}>
                      {orderTime(o.created_at)}
                    </small>
                  </td>
                  <td className="orders-actions-cell">
                    <div className="orders-actions-buttons">
                      <Button
                        variant="info"
                        size="sm"
                        onClick={() => openQr(o)}
                        title={tr("Show / print QR")}
                      >
                        <i className="fas fa-qrcode"></i>
                      </Button>
                      <Button
                        variant="warning"
                        size="sm"
                        onClick={() => openEdit(o)}
                        title={tr("Edit order")}
                      >
                        <i className="fas fa-edit"></i>
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDelete(o)}
                        title={tr("Delete order")}
                      >
                        <i className="fas fa-trash"></i>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <OrderDetailModal
        show={detailShow}
        onHide={() => setDetailShow(false)}
        order={detailOrder}
        stationName={stationName}
        onShowQr={openQrFromDetail}
        onEdit={openEditFromDetail}
        onDelete={handleDeleteFromDetail}
      />

      <OrderModal
        show={orderShow}
        onHide={() => setOrderShow(false)}
        stations={stations}
        getToken={authToken}
        onSaved={() => fetchOrders(stationFilter)}
        stationName={stationName}
        existingOrders={orders}
      />
      <OrderEditModal
        show={editShow}
        onHide={() => setEditShow(false)}
        order={editing}
        stations={stations}
        getToken={authToken}
        onSaved={() => fetchOrders(stationFilter)}
        stationName={stationName}
      />
      <OrderQrModal
        show={qrShow}
        onHide={() => setQrShow(false)}
        order={qrOrder}
        stationName={stationName}
      />
    </div>
  );
};

export default Orders;